import {
  CheckOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import {
  Col,
  Form,
  Popconfirm,
  Popover,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContextButton,
  IconContextButton,
  ScopeButton,
} from '../components/buttons';
import { CardTitleSpan, GraySpan } from '../components/formSpans';
import { ScopeTabs } from '../components/ScopeTabs';
import { SSOImg } from '../components/SSOImg';
import { AccountTypeTag } from '../users/AccountTypeTag';
import { IconCross, IconEdit, IconEllipses, IconUser } from '../users/icons';
import { ColoredTag } from '../users/tags';
import { AccountType, resolveAccountType } from '../users/types';
import { UserLoginHistory } from '../users/UserLoginHistory';
import { BillingForm } from './BillingForm';
import { ChangePasswordForm } from './ChangePasswordForm';
import { ProfileCard } from './ProfileCard';
import type {
  AccountCountry,
  AccountLicence,
  AccountProfileUser,
  BillingDetails,
  LoginDetails,
  ThemePreferenceValue,
} from './types';

const PROFILE_TAB_ACCOUNT_SECURITY = 'account-security';
const PROFILE_TAB_BILLING_DETAILS = 'billing-details';
const PROFILE_TAB_LOGIN_HISTORY = 'login-history';

export type AccountProfileProps = {
  loading: boolean;
  currentUser?: AccountProfileUser;
  selectedAccount?: AccountProfileUser;
  history?: LoginDetails[];
  connectXeroAccount: () => void;
  connectQboAccount: () => void;
  disconnectXeroAccount: () => void;
  disconnectQboAccount: () => void;
  openMFAWindow: () => void;
  removeMfa: (userId: string) => void;
  deleteProfile: () => void;
  savePassword: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string,
  ) => void;
  openAccessRights: (accountId: string) => void;
  openEditImageWindow: (mode?: 'preview' | 'edit') => void;
  onDeleteUser: (userToDeleteId: string) => void;
  countries: AccountCountry[];
  openEditDetails: () => void;
  openChangeOwnerForm: () => void;
  redirectPage: (url: string) => void;
  billingDetails?: BillingDetails;
  saveBillingDetails: (details: BillingDetails) => void;
  userLicence?: AccountLicence;
  themePreference: ThemePreferenceValue;
  onThemePreferenceChange: (value: ThemePreferenceValue) => void;
  xeroLogoSrc: string;
  qboLogoSrc: string;
  defaultAvatarSrc: string;
  upgradeBanner?: ReactNode;
  pricingPageLink?: string;
  /** Optional date-time formatter for last login / login history. Defaults to DD-MM-YYYY HH:mm:ss. */
  formatDateTime?: (value?: Date | string | number) => string;
};

const isUnlimitedStatus = (status?: string | null) =>
  String(status ?? '').toUpperCase() === 'UNLIMITED';

const isTrialLicence = (licenseType?: string | null) =>
  String(licenseType ?? '').toUpperCase() === 'TRIAL';

const formatLoginTs = (value?: Date | string | number) => {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const AccountProfile = ({
  loading,
  currentUser,
  selectedAccount,
  history,
  connectXeroAccount,
  connectQboAccount,
  disconnectXeroAccount,
  disconnectQboAccount,
  openMFAWindow,
  removeMfa,
  deleteProfile,
  savePassword,
  openAccessRights,
  openEditImageWindow,
  onDeleteUser,
  countries,
  openEditDetails,
  openChangeOwnerForm,
  redirectPage,
  billingDetails,
  saveBillingDetails,
  userLicence,
  themePreference,
  onThemePreferenceChange,
  xeroLogoSrc,
  qboLogoSrc,
  defaultAvatarSrc,
  upgradeBanner,
  pricingPageLink = '/pricing',
  formatDateTime = formatLoginTs,
}: AccountProfileProps) => {
  const { t } = useTranslation();
  const [formPassword] = Form.useForm();
  const [billingForm] = Form.useForm();
  const [isAllowed, setIsAllowed] = useState(false);
  const values = Form.useWatch([], billingForm);

  useEffect(() => {
    let isMounted = true;
    billingForm
      .validateFields({ validateOnly: true })
      .then(() => {
        if (isMounted) setIsAllowed(true);
      })
      .catch(() => {
        if (isMounted) setIsAllowed(false);
      });
    return () => {
      isMounted = false;
    };
  }, [billingForm, values]);

  useEffect(() => {
    if (billingDetails) {
      billingForm.setFieldsValue(billingDetails);
    }
  }, [billingDetails, billingForm]);

  const profilePicture = selectedAccount?.profilePicture || defaultAvatarSrc;
  const isCurrentUser = !selectedAccount?.userUuid
    ? !!currentUser?.userUuid
    : selectedAccount.userUuid === currentUser?.userUuid;

  const renderAccountCard = () => (
    <div className="account-section-card">
      <div className="account-section-card-inner">
        <Space direction="vertical" style={{ rowGap: 4, minWidth: 422, flex: 1 }}>
          <CardTitleSpan>{t('common.account')}</CardTitleSpan>
          <Space style={{ columnGap: 15 }}>
            <GraySpan>
              {`${t('users.ip-address')}: ${history?.[0]?.loginIP ?? ''}`}
            </GraySpan>
            <span>.</span>
            <GraySpan>
              {`${t('users.last-login')}: ${formatDateTime(history?.[0]?.loginTimestamp)}`}
            </GraySpan>
          </Space>
        </Space>
        <a
          target="_blank"
          href={`https://whatismyipaddress.com/ip/${history?.[0]?.loginIP ?? ''}`}
          rel="noreferrer"
        >
          <ScopeButton type="default" width="154px" height="32px">
            {t('users.verify-location')}
          </ScopeButton>
        </a>
      </div>
    </div>
  );

  const renderUserPermissionsCard = () => (
    <div className="account-section-card">
      <div className="account-section-card-inner">
        <Space direction="vertical" style={{ rowGap: 4, minWidth: 446, flex: 1 }}>
          <CardTitleSpan>{t('users.user-permissions')}</CardTitleSpan>
          <GraySpan>{t('other.entity-access-description')}</GraySpan>
        </Space>
        <ScopeButton
          type="default"
          width="154px"
          height="32px"
          onClick={() => openAccessRights(selectedAccount?.accountUuid || '')}
        >
          {t('users.access-rights')}
        </ScopeButton>
      </div>
    </div>
  );

  const enabledTag = (enabled: boolean) =>
    enabled ? (
      <ColoredTag
        color="var(--color-status-success, #058181)"
        bgcolor="var(--color-status-success-bg, #E9F6F7)"
        bordered
        bordercolor="var(--color-border, #dbeae3)"
        icon={<CheckOutlined />}
      >
        {t('common.enabled')}
      </ColoredTag>
    ) : (
      <ColoredTag
        color="var(--color-text-muted, #5a6a6a)"
        bgcolor="var(--color-surface, #f3f7f8)"
        bordered
        bordercolor="var(--color-border, #dbeae3)"
      >
        {t('common.not-enabled')}
      </ColoredTag>
    );

  const providerRow = (
    name: string,
    logo: string,
    enabled?: boolean,
    onConnect?: () => void,
    onDisconnect?: () => void,
    confirmKey?: string,
  ) => (
    <div className="account-provider-row">
      <Space style={{ columnGap: 20, minWidth: 220 }}>
        <SSOImg src={logo} alt={name} width="31px" $padding="3px" $noAbsolute />
        <CardTitleSpan style={{ fontSize: 14, fontWeight: 500 }}>{name}</CardTitleSpan>
      </Space>
      {isCurrentUser ? (
        enabled ? (
          <Popconfirm
            title={t('other.disconnect-account')}
            description={confirmKey ? t(confirmKey) : undefined}
            onConfirm={onDisconnect}
            placement="top"
            okText={t('common.yes')}
            cancelText={t('common.cancel')}
          >
            <ScopeButton type="default" width="154px" height="32px">
              {t('common.disconnect')}
            </ScopeButton>
          </Popconfirm>
        ) : (
          <ScopeButton type="primary" width="154px" height="32px" onClick={onConnect}>
            {t('common.connect')}
          </ScopeButton>
        )
      ) : (
        <div style={{ width: 154, display: 'flex', justifyContent: 'flex-end' }}>
          {enabledTag(!!enabled)}
        </div>
      )}
    </div>
  );

  const renderAssociatedAccountsCard = () => (
    <div className="account-section-card">
      <Space direction="vertical" style={{ rowGap: 30, width: '100%' }}>
        <Space direction="vertical" style={{ rowGap: 4 }}>
          <CardTitleSpan>{t('users.associated-accounts')}</CardTitleSpan>
          <GraySpan style={{ width: 460, display: 'block' }}>
            {t('users.associated-accounts-desc')}
          </GraySpan>
        </Space>
        <Space direction="vertical">
          {providerRow(
            t('users.xero-accounting'),
            xeroLogoSrc,
            selectedAccount?.xeroEnabled,
            connectXeroAccount,
            disconnectXeroAccount,
            'other.disconnect-xero-confirm',
          )}
          {providerRow(
            t('users.qbo-accounting'),
            qboLogoSrc,
            selectedAccount?.quickbooksEnabled,
            connectQboAccount,
            disconnectQboAccount,
            'other.disconnect-quickbooks-confirm',
          )}
        </Space>
      </Space>
    </div>
  );

  const renderMFACard = () => (
    <div className="account-section-card">
      <div className="account-section-card-inner">
        <Space direction="vertical" style={{ rowGap: 4, flex: 1 }}>
          <CardTitleSpan>{t('users.mfa-authentication')}</CardTitleSpan>
          <GraySpan>{t('users.mfa-authentication-desc')}</GraySpan>
        </Space>
        <Space style={{ marginRight: 8 }}>
          <GraySpan>
            {selectedAccount?.mfa ? t('common.on') : t('common.off')}
          </GraySpan>
          <Tooltip
            title={
              selectedAccount?.mfa
                ? t('common.toggle-on-click-to-turn-off')
                : t('common.toggle-off-click-to-turn-on')
            }
          >
            <span>
              <Switch
                aria-label={t('users.mfa-authentication')}
                className={selectedAccount?.mfa ? 'switch-checked' : ''}
                checked={!!selectedAccount?.mfa}
                disabled={
                  !isCurrentUser && !selectedAccount?.mfa && !currentUser?.canManageUsers
                }
                onChange={() => {
                  if (selectedAccount?.mfa) {
                    removeMfa(selectedAccount.userUuid);
                  } else if (isCurrentUser) {
                    openMFAWindow();
                  }
                }}
              />
            </span>
          </Tooltip>
        </Space>
      </div>
    </div>
  );

  const renderPasswordCard = () => (
    <div className="account-section-card">
      <Space direction="vertical" style={{ rowGap: 4 }}>
          <CardTitleSpan>{t('users.change-password')}</CardTitleSpan>
        <Space style={{ columnGap: 15, marginTop: 25 }}>
          <ChangePasswordForm
            hasPassword={currentUser?.hasPassword}
            savePassword={savePassword}
            form={formPassword}
          />
        </Space>
      </Space>
    </div>
  );

  const renderThemeCard = () => (
    <div className="account-section-card">
      <div className="account-section-card-inner">
        <Space direction="vertical" style={{ rowGap: 4, flex: 1 }}>
          <CardTitleSpan>{t('users.theme-label')}</CardTitleSpan>
          <GraySpan>{t('users.theme-help')}</GraySpan>
        </Space>
        <Radio.Group
          value={themePreference}
          onChange={(e) =>
            onThemePreferenceChange(e.target.value as ThemePreferenceValue)
          }
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="light">{t('users.theme-light')}</Radio.Button>
          <Radio.Button value="dark">{t('users.theme-dark')}</Radio.Button>
        </Radio.Group>
      </div>
    </div>
  );

  const accountType = resolveAccountType(selectedAccount?.accountType);
  const renderAccountAndSecurityTab = () => (
    <Space direction="vertical" style={{ rowGap: 24, marginTop: 25 }}>
      {renderAccountCard()}
      {selectedAccount &&
        accountType !== AccountType.STAFF &&
        accountType !== AccountType.CLIENT &&
        renderUserPermissionsCard()}
      {renderAssociatedAccountsCard()}
      {renderMFACard()}
      {isCurrentUser && renderThemeCard()}
      {isCurrentUser && renderPasswordCard()}
    </Space>
  );

  const renderUserSubscription = () => {
    if (
      !selectedAccount ||
      (accountType !== AccountType.OWNER && accountType !== AccountType.STAFF) ||
      isUnlimitedStatus(userLicence?.status)
    ) {
      return null;
    }
    const isTrial = isTrialLicence(userLicence?.licenseType);
    const active = (userLicence?.licenseDaysLeft ?? 0) > 0;
    return (
      <ColoredTag
        color="var(--color-surface-base, #ffffff)"
        bgcolor={
          isTrial || active
            ? 'var(--color-license-trial, #af41aa)'
            : 'var(--color-status-warning, #c38a20)'
        }
        style={{
          marginInlineEnd: 0,
          padding: '2px 6px',
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 0,
        }}
      >
        {isTrial
          ? t('navigation.subscription-free-trial')
          : active
            ? t('navigation.subscription-subscribed')
            : t('navigation.subscription-overdue')}
      </ColoredTag>
    );
  };

  const renderContextMenu = (record: AccountProfileUser) => (
    <div className="context-menu-items">
      <Space direction="vertical" style={{ rowGap: 0 }}>
        {isCurrentUser && (
          <IconContextButton
            type="text"
            icon={<IconEdit style={{ width: 16, height: 17, marginRight: 7 }} />}
            onClick={openEditDetails}
          >
            {t('organisation.edit-details')}
          </IconContextButton>
        )}
        {resolveAccountType(currentUser?.accountType) === AccountType.OWNER && (
          <IconContextButton
            type="text"
            icon={<IconUser style={{ width: 18, height: 20, marginRight: 4 }} />}
            onClick={openChangeOwnerForm}
          >
            {t('users.change-owner')}
          </IconContextButton>
        )}
        {!record?.isLicenseOwner && record?.userUuid !== currentUser?.userUuid ? (
          currentUser?.canManageUsers && record.userUuid ? (
            <Popconfirm
              title={t('users.delete-user-title')}
              description={
                <span>
                  {t('users.delete-user-description').split('{br}')[0]}
                  <br />
                  {t('users.delete-user-description').split('{br}')[1]}
                </span>
              }
              onConfirm={() => onDeleteUser(record.userUuid)}
              okText={t('common.yes')}
              cancelText={t('common.cancel')}
            >
              <IconContextButton
                type="text"
                icon={<IconCross style={{ width: 10, height: 22, marginRight: 8, marginLeft: 5 }} />}
              >
                {t('other.delete-account')}
              </IconContextButton>
            </Popconfirm>
          ) : null
        ) : (
          <Popconfirm
            title={t('other.delete-account-title')}
            description={
              <>
                <Typography.Paragraph>
                  {t('other.delete-account-line-1')}
                  <br />
                  {t('other.delete-account-line-2')}
                  <br />
                  {t('other.delete-account-line-3')}
                  <br />
                  {t('other.delete-account-line-4')}
                  <br />
                  {t('other.delete-account-line-5')}
                  <br />
                  {t('other.delete-account-line-6')}
                </Typography.Paragraph>
                {t('other.delete-account-confirm')}
              </>
            }
            okText={t('common.yes')}
            cancelText={t('common.no')}
            onConfirm={deleteProfile}
          >
            <IconContextButton
              type="text"
              icon={<IconCross style={{ width: 10, height: 22, marginRight: 8, marginLeft: 5 }} />}
            >
              {t('other.delete-account')}
            </IconContextButton>
          </Popconfirm>
        )}
      </Space>
    </div>
  );

  const profileContextMenu =
    resolveAccountType(currentUser?.accountType) === AccountType.OWNER &&
    currentUser?.accountUuid === selectedAccount?.accountUuid &&
    selectedAccount ? (
      <Popover
        placement="topLeft"
        arrow={false}
        trigger="click"
        content={renderContextMenu(selectedAccount)}
        overlayStyle={{ zIndex: 999 }}
      >
        <ContextButton type="text" aria-label={t('common.more-actions')}>
          <IconEllipses aria-hidden style={{ width: 18, height: 18 }} />
        </ContextButton>
      </Popover>
    ) : null;

  const getFullPhoneNumber = () => {
    let phoneNumber = '';
    if (selectedAccount?.phonePrefix) phoneNumber = `${selectedAccount.phonePrefix} `;
    phoneNumber += selectedAccount?.phoneNumber ?? '';
    return phoneNumber;
  };

  const showBillingTab =
    isCurrentUser &&
    resolveAccountType(selectedAccount?.accountType) === AccountType.OWNER &&
    !isUnlimitedStatus(userLicence?.status);

  const items = [
    {
      label: (
        <span>
          <SafetyOutlined style={{ marginRight: 8 }} />
          {t('users.account-security')}
        </span>
      ),
      key: PROFILE_TAB_ACCOUNT_SECURITY,
      children: renderAccountAndSecurityTab(),
    },
    {
      label: (
        <span>
          <CreditCardOutlined style={{ marginRight: 8 }} />
          {t('users.billing-details')}
        </span>
      ),
      key: PROFILE_TAB_BILLING_DETAILS,
      children: (
        <BillingForm
          billingForm={billingForm}
          countries={countries}
          isLoading={loading}
          isAllowed={isAllowed}
          saveBillingDetails={saveBillingDetails}
        />
      ),
    },
    {
      label: (
        <span>
          <HistoryOutlined style={{ marginRight: 8 }} />
          {t('users.login-history')}
        </span>
      ),
      key: PROFILE_TAB_LOGIN_HISTORY,
      children: (
        <div className="account-section-card account-login-history-card">
          <UserLoginHistory
            history={history}
            profileMode
            onClose={() => {}}
            loading={loading}
            formatTimestamp={formatDateTime}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="account-profile-root">
      <Spin size="large" spinning={loading}>
        <Row>
          <Col
            span={20}
            xs={{ offset: 0 }}
            sm={{ offset: 0 }}
            md={{ offset: 0 }}
            lg={{ offset: 0 }}
            xl={{ offset: 0, span: 24 }}
            xxl={{ offset: 2, span: 16 }}
          >
            <Space align="start" style={{ columnGap: 25 }}>
              <div style={{ height: '100vh', position: 'fixed' }}>
                <ProfileCard
                  selectedAccount={selectedAccount}
                  currentUser={currentUser}
                  profilePicture={profilePicture}
                  defaultAvatarSrc={defaultAvatarSrc}
                  openEditImageWindow={openEditImageWindow}
                  renderUserSubscription={renderUserSubscription}
                  renderRole={(content) => <AccountTypeTag accountTypeKey={content} t={t} />}
                  getFullPhoneNumber={getFullPhoneNumber}
                  redirectPage={redirectPage}
                  openEditDetails={openEditDetails}
                  userLicence={userLicence}
                  pricingPageLink={pricingPageLink}
                />
              </div>
              <div>
                {upgradeBanner}
                <div className="account-profile-main-card">
                  <div style={{ position: 'relative' }}>
                    {profileContextMenu && (
                      <div style={{ position: 'absolute', top: 12, right: 0, zIndex: 2 }}>
                        {profileContextMenu}
                      </div>
                    )}
                    <ScopeTabs
                      defaultActiveKey={PROFILE_TAB_ACCOUNT_SECURITY}
                      tabBarStyle={profileContextMenu ? { paddingRight: 40 } : undefined}
                      items={
                        showBillingTab
                          ? items
                          : items.filter((item) => item.key !== PROFILE_TAB_BILLING_DETAILS)
                      }
                    />
                  </div>
                </div>
              </div>
            </Space>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
